// ============================================================================
// statusTone — the one map from a status word to its badge tone
// (Design Constitution §4 Badges: L18, Q22, Q10, A42; F07).
// ----------------------------------------------------------------------------
// Planned / Draft / Forecast = neutral · Active / In Process = info ·
// Complete = ok · Needs Attention / Queued / Pending = warn ·
// Problem / Stuck / Overdue = danger (L18). Q22 adds: Inactive, Sold, Retired,
// Hidden, Deceased, Skipped, Expired = neutral; Processed, Sent To Trip,
// Transferred = ok; grazing Occupied info, Resting warn, Ready ok, No History
// neutral, Invalid danger. On-farm animals get no badge (the caller renders
// none). Q10: a due date reached or missed is danger ('Due', 'Missed');
// approaching dates use a warn reason badge ('Due In 5 Days') built by the
// caller. An unknown word is neutral, never a guessed color.
// ============================================================================

export type StatusTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

const TONE_BY_STATUS: Readonly<Record<string, StatusTone>> = {
  // neutral
  planned: 'neutral',
  draft: 'neutral',
  forecast: 'neutral',
  projected: 'neutral',
  inactive: 'neutral',
  sold: 'neutral',
  retired: 'neutral',
  hidden: 'neutral',
  deceased: 'neutral',
  skipped: 'neutral',
  expired: 'neutral',
  'no history': 'neutral',
  // info
  active: 'info',
  'in process': 'info',
  occupied: 'info',
  // ok
  complete: 'ok',
  completed: 'ok',
  processed: 'ok',
  'sent to trip': 'ok',
  transferred: 'ok',
  ready: 'ok',
  // warn
  'needs attention': 'warn',
  queued: 'warn',
  pending: 'warn',
  'awaiting approval': 'warn',
  resting: 'warn',
  // danger
  problem: 'danger',
  stuck: 'danger',
  overdue: 'danger',
  due: 'danger',
  missed: 'danger',
  'needs help': 'danger',
  invalid: 'danger',
};

export function normalizeStatus(word: string | null | undefined): string {
  return String(word ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function statusTone(word: string | null | undefined): StatusTone {
  return TONE_BY_STATUS[normalizeStatus(word)] ?? 'neutral';
}

// Title Case for a status word ('in_process' -> 'In Process'), §5 wording.
export function statusLabel(word: string | null | undefined): string {
  return normalizeStatus(word).replace(/\b[a-z]/g, (c) => c.toUpperCase());
}
